/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { unloadRecord } from 'uplisting-frontend/utils/changeset';
import CreateTaxComponent from 'uplisting-frontend/components/ui/create-tax';

interface IArgs {
  currency: string;
  booking: ModelRegistry['booking'];
}

export interface BookingPriceAdditionalChargeSignature {
  Element: null;

  Args: IArgs;
}

export default class UiBookingPriceAdditionalChargeComponent extends Component<BookingPriceAdditionalChargeSignature> {
  @service store!: Services['store'];
  @service notifications!: Services['notifications'];

  @service('repositories/tax') taxRepository!: Services['repositories/tax'];

  @service('repositories/additional-booking-charge')
  additionalBookingChargeRepository!: Services['repositories/additional-booking-charge'];

  isSaving = false;

  @tracked showCreateModal = false;

  get createTaxComponent(): typeof CreateTaxComponent {
    return CreateTaxComponent;
  }

  @tracked
  changesets!: GenericChangeset<ModelRegistry['additional-booking-charge']>[];

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get charges(): ModelRegistry['additional-booking-charge'][] {
    return this.booking.pendingAdditionalCharges;
  }

  @cached
  get isAnyInvalid(): boolean {
    const { changesets } = this;

    if (!changesets) {
      return false;
    }

    return changesets.some((changeset) => changeset.isInvalid);
  }

  @action
  handleShowCreateModal(): void {
    const { charges, additionalBookingChargeRepository } = this;

    if (charges.length) {
      this.changesets = charges.map((charge) => {
        const changeset =
          additionalBookingChargeRepository.buildChangeset(charge);

        if (!changeset?.tax) {
          changeset.tax = this.taxRepository.defaultTaxOption;
        }

        return changeset;
      });
    } else {
      this.changesets = [this.createCharge()];
    }

    this.showCreateModal = true;
  }

  @action
  handleClose(): void {
    if (!this.isSaving) {
      this.changesets.map((changeset) => {
        unloadRecord<ModelRegistry['additional-booking-charge']>(changeset);
      });
    }

    this.showCreateModal = false;
  }

  @action
  handleRemoveChargeAt(index: number): void {
    unloadRecord<ModelRegistry['additional-booking-charge']>(
      this.changesets[index] as GenericChangeset<
        ModelRegistry['additional-booking-charge']
      >,
    );

    this.changesets.splice(index, 1);

    this.changesets = [...this.changesets];
  }

  @action
  handleAddCharge(): void {
    this.changesets = [...this.changesets, this.createCharge()];
  }

  @action
  @waitFor
  async handleSubmit(): Promise<void> {
    const { changesets } = this;

    await Promise.all(changesets.map((changeset) => changeset.validate()));

    if (this.isAnyInvalid) {
      return;
    }

    changesets.map((changeset) => changeset.execute());

    const bulkCharge = this.store.createRecord(
      'bulk-additional-booking-charge',
      {
        booking: this.booking,
        additionalBookingCharges: changesets.map((item) => item.data),
      },
    );

    try {
      await bulkCharge.save();

      this.notifications.info(
        'action_bookings_price.additional_charge_modal.notifications.saved',
      );

      await this.booking.reload();

      this.handleClose();
    } catch {
      this.notifications.error();
    }
  }

  private createCharge(): GenericChangeset<
    ModelRegistry['additional-booking-charge']
  > {
    const { additionalBookingChargeRepository } = this;
    const record = additionalBookingChargeRepository.createRecord({
      tax: this.taxRepository.defaultTaxOption,
    });

    return additionalBookingChargeRepository.buildChangeset(record);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price::AdditionalCharge': typeof UiBookingPriceAdditionalChargeComponent;
  }
}
